import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import dataService from '../services/dataService';
import { toast } from 'react-toastify';
import {
    ChartBarIcon,
    CalendarIcon,
    CurrencyRupeeIcon,
    ArrowUpRightIcon,
    ArrowDownLeftIcon,
    LinkIcon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

const ReferralsSummary = () => {
    const { user } = useAuth();
    const [summary, setSummary] = useState({
        referencesPassed: 0,
        businessReceived: 0,
        businessGiven: 0
    });
    const [allMembers, setAllMembers] = useState([]);
    const [period, setPeriod] = useState('general');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSummary();
    }, [period]);

    const fetchSummary = async () => {
        try {
            setLoading(true);
            const res = await dataService.getReferralSummary(period, true); // Pass true for allMembers
            if (res.data.success) {
                setSummary(res.data.data.personal);
                setAllMembers(res.data.data.allMembers || []);
            }
        } catch (error) {
            console.error('Failed to load summary', error);
            toast.error('Failed to load summary');
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, icon: Icon, color, subText, isCurrency = false }) => (
        <div className="bg-white p-8 rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 flex flex-col gap-6 group hover:scale-[1.02] transition-all duration-300">
            <div className="flex justify-between items-start">
                <div className={clsx("p-4 rounded-2xl shadow-lg transition-transform group-hover:rotate-6", color)}>
                    <Icon className="w-8 h-8 text-white" />
                </div>
                <div className="flex flex-col items-end">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">{title}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                        {isCurrency && <CurrencyRupeeIcon className="w-5 h-5 text-gray-900" />}
                        <h2 className="text-4xl font-black text-gray-900 tracking-tighter italic">
                            {isCurrency ? parseFloat(value || 0).toLocaleString('en-IN') : (value || 0)}
                        </h2>
                    </div>
                </div>
            </div>
            <div className="pt-4 border-t border-gray-50">
                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest leading-relaxed">
                    {subText}
                </p>
            </div>
        </div>
    );

    return (
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-1">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary-50 rounded-xl">
                            <ChartBarIcon className="w-6 h-6 text-primary-600" />
                        </div>
                        <h1 className="text-4xl font-black text-gray-900 tracking-tight italic">Reference Summary</h1>
                    </div>
                    <p className="text-sm font-bold text-gray-500 uppercase tracking-widest ml-11">Performance analytics for your networking</p>
                </div>

                <div className="flex items-center gap-4 bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
                    <CalendarIcon className="w-5 h-5 text-gray-400 ml-2" />
                    <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="bg-transparent border-none focus:ring-0 text-sm font-black text-gray-900 uppercase tracking-widest cursor-pointer pr-10"
                    >
                        <option value="general">General (All Time)</option>
                        <option value="month">Last 30 Days</option>
                        <option value="week">Last 7 Days</option>
                    </select>
                </div>
            </div>

            {/* All Members Table */}
            <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
                <div className="px-10 py-8 border-b border-gray-50 bg-gray-50/50">
                    <h3 className="text-xl font-black text-gray-900 tracking-tight italic">Member Summary List</h3>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Detailed performance tracking of all members</p>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-white border-b border-gray-100">
                                <th className="px-10 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest">Member Name</th>
                                <th className="px-10 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest text-center">Passed</th>
                                <th className="px-10 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest text-right">Received (₹)</th>
                                <th className="px-10 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest text-right">Send (₹)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                [1, 2, 3, 4, 5].map(i => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="4" className="px-10 py-6 h-16 bg-gray-50/20" />
                                    </tr>
                                ))
                            ) : allMembers.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-10 py-12 text-center text-gray-400 font-bold italic uppercase tracking-widest opacity-50">
                                        No members found
                                    </td>
                                </tr>
                            ) : (
                                allMembers.map((member) => (
                                    <tr key={member.id} className="group hover:bg-gray-50/50 transition-colors">
                                        <td className="px-10 py-5">
                                            <div className="flex items-center gap-3">
                                                {member.profile_image ? (
                                                    <img src={member.profile_image} alt={member.name} className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm" />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 font-black text-xs">
                                                        {member.name?.charAt(0)}
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-black text-gray-900 leading-tight">{member.name}</p>
                                                    <p className="text-[10px] font-bold text-primary-600 uppercase tracking-tighter italic">{member.chapter || 'No Chapter'}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-10 py-5 text-center">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-black uppercase tracking-widest">
                                                {member.referencesPassed || 0}
                                            </span>
                                        </td>
                                        <td className="px-10 py-5 text-right font-black text-emerald-600 tracking-tight">
                                            ₹{parseFloat(member.businessGiven || 0).toLocaleString('en-IN')}
                                        </td>
                                        <td className="px-10 py-5 text-right font-black text-amber-600 tracking-tight">
                                            ₹{parseFloat(member.businessReceived || 0).toLocaleString('en-IN')}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ReferralsSummary;
